import { z } from "zod";
import { MAX_BILL_LIMIT_USD, PAID_PLAN_IDS, PLANS } from "../billing/plans.js";
import { PAYING_STATUSES, PAYMENT_PROBLEM_STATUSES } from "../billing/stripe.js";
import { type TenantId, actionIdSchema, tenantIdSchema } from "./ids.js";
import { LIMITS, emailSchema } from "./common.js";

export const TENANT_STATES = ["active", "paused"] as const;
export type TenantState = (typeof TENANT_STATES)[number];

export const APPROVAL_NOTICES = ["immediate", "digest"] as const;
export type ApprovalNotice = (typeof APPROVAL_NOTICES)[number];

export const tenantCapsSchema = z.strictObject({
  messagesPerDay: z.int().min(1).max(100_000),
  runsPerDay: z.int().min(1).max(100_000),
  outboundPerDay: z.int().min(1).max(10_000),
  writerCallsPerDay: z.int().min(1).max(10_000),
});
export type TenantCaps = z.infer<typeof tenantCapsSchema>;

export const tenantSettingsSchema = z.strictObject({
  threshold: z.number().min(0.4).max(0.9),
  /** Hour of the day, in UTC, when the daily digest goes out. */
  digestHour: z.int().min(0).max(23),
  approvalNotice: z.enum(APPROVAL_NOTICES),
  caps: tenantCapsSchema,
});
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

/** A member's correction, shown to the router as a past example. */
export const routeExampleSchema = z.strictObject({
  text: z.string().max(LIMITS.textPreview),
  actionId: actionIdSchema,
});
export type RouteExample = z.infer<typeof routeExampleSchema>;

export const tenantNameSchema = z.string().trim().min(1).max(LIMITS.tenantName);
export const instructionsSchema = z.string().max(LIMITS.instructions);

export const billLimitUsdSchema = z.int().min(1).max(MAX_BILL_LIMIT_USD);

/**
 * The tenant's plan and its Stripe objects, in one of three states:
 * - `free`: no subscription. A tenant that paid before keeps its customer, so
 *   housekeeping can still report the last period's overage.
 * - `paid`: a subscription that pays (`active`, `trialing`, `past_due`).
 * - `paymentProblem`: a subscription that still exists but does not pay. The
 *   tenant is on free limits until the member pays it or starts a new plan.
 */
export const tenantBillingSchema = z.discriminatedUnion("state", [
  z.strictObject({
    state: z.literal("free"),
    plan: z.literal("free"),
    stripeCustomerId: z.string().nullable(),
  }),
  z.strictObject({
    state: z.literal("paid"),
    plan: z.enum(PAID_PLAN_IDS),
    billLimitUsd: billLimitUsdSchema,
    stripeCustomerId: z.string(),
    stripeSubscriptionId: z.string(),
    subscriptionStatus: z.enum(PAYING_STATUSES),
    /** The plan goes back to free when the period ends. */
    cancelAtPeriodEnd: z.boolean(),
  }),
  z.strictObject({
    state: z.literal("paymentProblem"),
    plan: z.literal("free"),
    stripeCustomerId: z.string(),
    stripeSubscriptionId: z.string(),
    subscriptionStatus: z.enum(PAYMENT_PROBLEM_STATUSES),
    /** The plan the subscription is for, once it is paid. */
    subscribedPlan: z.enum(PAID_PLAN_IDS),
  }),
]);
export type TenantBilling = z.infer<typeof tenantBillingSchema>;
export const BILLING_STATES = ["free", "paid", "paymentProblem"] as const;
export type BillingState = (typeof BILLING_STATES)[number];

export const NEW_TENANT_BILLING: TenantBilling = { state: "free", plan: "free", stripeCustomerId: null };

export const tenantSchema = z.strictObject({
  id: tenantIdSchema,
  name: tenantNameSchema,
  state: z.enum(TENANT_STATES),
  instructions: instructionsSchema,
  settings: tenantSettingsSchema,
  billing: tenantBillingSchema,
  /** Bumped by every billing write. A billing write is conditional on the version it read. */
  billingVersion: z.int().min(0),
  examples: z.array(routeExampleSchema).max(LIMITS.examples),
  createdAt: z.iso.datetime(),
});
export type Tenant = z.infer<typeof tenantSchema>;

/** Body of `PATCH /tenant`. Every field is optional; at least one is required. */
export const tenantPatchSchema = z
  .strictObject({
    name: tenantNameSchema.optional(),
    instructions: instructionsSchema.optional(),
    threshold: tenantSettingsSchema.shape.threshold.optional(),
    digestHour: tenantSettingsSchema.shape.digestHour.optional(),
    approvalNotice: tenantSettingsSchema.shape.approvalNotice.optional(),
    state: z.enum(TENANT_STATES).optional(),
    caps: tenantCapsSchema.partial().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "Send at least one field" });
export type TenantPatch = z.infer<typeof tenantPatchSchema>;

export const memberSchema = z.strictObject({
  email: emailSchema,
  role: z.literal("owner"),
  joinedAt: z.iso.datetime(),
});
export type Member = z.infer<typeof memberSchema>;

/** Settings for a new tenant. The caps are the free plan's; a plan change resets them to the new plan's. */
export const NEW_TENANT_SETTINGS: TenantSettings = {
  threshold: 0.6,
  digestHour: 14,
  approvalNotice: "immediate",
  caps: PLANS.free.caps,
};

/** A new tenant: free, with the free plan's settings and no examples. */
export function newTenant(id: TenantId, name: string, createdAt: Date): Tenant {
  return { id, name, state: "active", instructions: "", settings: NEW_TENANT_SETTINGS, billing: NEW_TENANT_BILLING, billingVersion: 0, examples: [], createdAt: createdAt.toISOString() };
}
